/**
 * Field-trip meal placements — school-admin self-serve. One dialog
 * submission either PLACES real bagged-meal orders (daily total ≤ 30 —
 * they appear in the Orders week view immediately) or opens a Support
 * ticket with the Ordo team (31+). `POST /api/school-orders/field-trips`.
 *
 * `placementId` is a client-generated uuid: the server keys its
 * per-batch upsert AND the Tasker `sourceRecordKey` on it, so retrying
 * a partial failure (or double-submitting) with the SAME id is
 * idempotent on both branches.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Mealtime } from '@/lib/mealtime'

import { apiFetch } from './http'

export type FieldTripGroupDimension = 'AGE_GROUP' | 'GRADE' | 'TEACHER' | 'ROOM' | 'HOMEROOM' | 'LUNCH_PERIOD'

/**
 * One targeted enrollment group. AGE_GROUP values carry the
 * LocationGradeRange column id; every other dimension carries the
 * campus vocabulary value label.
 */
export interface FieldTripGroupScope {
  dimension: FieldTripGroupDimension
  value: string
}

/**
 * A per-group daily quantity. `group: null` is the bucket-less
 * whole-school column (NONE campuses).
 */
export interface FieldTripGroupQuantity {
  group: FieldTripGroupScope | null
  quantity: number
}

/**
 * Per-meal quantities for one mealtime, sent when the day offers more than
 * one field-trip meal: which meal, and how many per group.
 */
export interface FieldTripMealSplit {
  mealtime: Mealtime
  menuItemId: string
  groupQuantities: FieldTripGroupQuantity[]
}

export interface CreateFieldTripInput {
  /** Client-generated uuid — idempotency key across retries. */
  placementId: string
  locationId: string
  /** YYYY-MM-DD, inclusive range. */
  startDate: string
  endDate: string
  mealtimes: Mealtime[]
  /**
   * Per-group daily quantities for every mealtime without `mealSplits`;
   * groups with 0 are omitted client-side. Empty when every mealtime is split.
   */
  groupQuantities: FieldTripGroupQuantity[]
  /** Per-meal quantities for the mealtimes that offer more than one meal. */
  mealSplits?: FieldTripMealSplit[]
  notes?: string
}

/** One placed (day, mealtime) order batch. */
export interface FieldTripPlacement {
  date: string
  mealtime: string
  batchId: string
  quantity: number
}

/** One blocked/failed (day, mealtime) with the server's reason. */
export interface FieldTripDayFailure {
  date: string
  mealtime: string | null
  error: string
  message: string
}

/**
 * A trip day the server skipped up-front (weekend / NO_SERVICE holiday).
 * Tolerates both a bare date string and an object shape on the wire.
 */
export type FieldTripSkippedDay = string | { date: string; reason?: string | null }

/**
 * Over-threshold review alert raised for the Ordo team alongside the placed
 * orders (absent/null under the threshold). `FAILED` = orders placed but
 * Tasker could not be reached — the UI asks the school to flag their contact.
 */
export type FieldTripAlert =
  | { status: 'RAISED'; ticketId: string; taskPath: string | null }
  | { status: 'FAILED'; reason: 'unconfigured' | 'unavailable' }

export type CreateFieldTripResponse =
  | {
      outcome: 'ORDERS_PLACED'
      placements: FieldTripPlacement[]
      skippedDays: FieldTripSkippedDay[]
      alert?: FieldTripAlert | null
    }
  | {
      outcome: 'ORDERS_PLACED_PARTIAL'
      placements: FieldTripPlacement[]
      failed: FieldTripDayFailure[]
      skippedDays: FieldTripSkippedDay[]
      alert?: FieldTripAlert | null
    }
  /** Legacy (pre-2026-09-17 servers): the 31+ "ticket instead of orders" outcome. Kept for deploy skew. */
  | { outcome: 'TICKET_CREATED'; ticketId: string; taskPath: string }

/**
 * Orders were (possibly partially) placed — the week view must refresh.
 * TICKET_CREATED places nothing, so nothing needs invalidating.
 */
export function fieldTripOutcomePlacedOrders(outcome: CreateFieldTripResponse['outcome']): boolean {
  return outcome === 'ORDERS_PLACED' || outcome === 'ORDERS_PLACED_PARTIAL'
}

/**
 * Defensive parse of the 422 `field_trip_blocked` body's `failures[]`
 * (per-(day, mealtime) pre-validation failures). Unknown shapes yield [].
 */
export function parseFieldTripBlockedFailures(body: unknown): FieldTripDayFailure[] {
  if (!body || typeof body !== 'object') {
    return []
  }
  const failures = (body as { failures?: unknown }).failures
  if (!Array.isArray(failures)) {
    return []
  }
  const out: FieldTripDayFailure[] = []
  for (const f of failures) {
    if (!f || typeof f !== 'object') {
      continue
    }
    const { date, mealtime, error, message } = f as Partial<FieldTripDayFailure>
    if (typeof date !== 'string') {
      continue
    }
    out.push({
      date,
      mealtime: typeof mealtime === 'string' ? mealtime : null,
      error: typeof error === 'string' ? error : 'blocked',
      message: typeof message === 'string' ? message : 'This day cannot be ordered.',
    })
  }
  return out
}

export async function createFieldTrip(input: CreateFieldTripInput): Promise<CreateFieldTripResponse> {
  return apiFetch<CreateFieldTripResponse>('/api/school-orders/field-trips', {
    method: 'POST',
    body: input,
  })
}

export function useCreateFieldTripMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createFieldTrip,
    onSuccess: (res) => {
      if (fieldTripOutcomePlacedOrders(res.outcome)) {
        // Real orders landed — the week view + month calendar read these.
        void qc.invalidateQueries({ queryKey: ['school-orders', 'forecast-batches'] })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Parent-order elections — "enable parent orders" for a field trip day
// ---------------------------------------------------------------------------

export type FieldTripElectionScopeKind = 'ALL' | 'GROUPS'

export interface FieldTripElectionWire {
  id: string
  locationId: string
  /** YYYY-MM-DD */
  serviceDate: string
  mealtime: string
  scopeKind: FieldTripElectionScopeKind
  groupScopes: FieldTripGroupScope[]
  /** "Disable all non-field-trip menu items on this day" for the in-scope students. */
  fieldTripOnly: boolean
  notes: string | null
  createdByUserId: string | null
  createdAt: string
}

export interface CreateFieldTripElectionInput {
  locationId: string
  serviceDate: string
  mealtime: Mealtime
  scopeKind: FieldTripElectionScopeKind
  groupScopes?: FieldTripGroupScope[]
  fieldTripOnly?: boolean
  notes?: string | null
}

export function fieldTripElectionsQueryKey(locationId: string) {
  return ['school-orders', 'field-trip-elections', locationId] as const
}

export async function listFieldTripElections(
  locationId: string,
  signal?: AbortSignal,
): Promise<FieldTripElectionWire[]> {
  const res = await apiFetch<{ elections: FieldTripElectionWire[] }>('/api/school-orders/field-trips/elections', {
    query: { locationId },
    signal,
  })
  return res.elections
}

/**
 * What saving a field-trip-only election did to orders already placed for its
 * in-scope students; null when the election is not field-trip-only.
 */
export interface FieldTripOnlySwitchResultWire {
  switched: Array<{ orderBatchId: string; studentUserIds: string[]; deltaCents: number }>
  notSwitched: Array<{ orderBatchId: string; studentUserIds: string[]; reason: string }>
}

export async function createFieldTripElection(
  input: CreateFieldTripElectionInput,
): Promise<{ election: FieldTripElectionWire; switchedOrders: FieldTripOnlySwitchResultWire | null }> {
  const res = await apiFetch<{
    election: FieldTripElectionWire
    switchedOrders?: FieldTripOnlySwitchResultWire | null
  }>('/api/school-orders/field-trips/elections', { method: 'POST', body: input })
  return { election: res.election, switchedOrders: res.switchedOrders ?? null }
}

export async function revokeFieldTripElection(id: string): Promise<FieldTripElectionWire> {
  const res = await apiFetch<{ election: FieldTripElectionWire }>(
    `/api/school-orders/field-trips/elections/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
  return res.election
}

export function useFieldTripElectionsQuery(locationId: string, opts?: { enabled?: boolean }) {
  return useQuery<FieldTripElectionWire[]>({
    queryKey: fieldTripElectionsQueryKey(locationId),
    queryFn: ({ signal }) => listFieldTripElections(locationId, signal),
    enabled: opts?.enabled ?? Boolean(locationId),
  })
}

export function useCreateFieldTripElectionMutation(locationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createFieldTripElection,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fieldTripElectionsQueryKey(locationId) })
    },
  })
}

export async function updateFieldTripElection(vars: {
  id: string
  fieldTripOnly: boolean
}): Promise<FieldTripElectionWire> {
  const res = await apiFetch<{ election: FieldTripElectionWire }>(
    `/api/school-orders/field-trips/elections/${encodeURIComponent(vars.id)}`,
    { method: 'PATCH', body: { fieldTripOnly: vars.fieldTripOnly } },
  )
  return res.election
}

export function useUpdateFieldTripElectionMutation(locationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateFieldTripElection,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fieldTripElectionsQueryKey(locationId) })
    },
  })
}

export function useRevokeFieldTripElectionMutation(locationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: revokeFieldTripElection,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: fieldTripElectionsQueryKey(locationId) })
    },
  })
}
